import { Card } from '@ordo/ui/card'
import { cn } from '@ordo/ui/cn'
import { Collapsible, CollapsibleContent } from '@ordo/ui/collapsible'
import { AlertTriangle, ChevronDown, Info } from 'lucide-react'
import { useMemo, useState } from 'react'

import { CampusInfoDialog } from '@/components/chefs/campus-info-dialog'
import { CampusMilkCard } from '@/components/chefs/campus-milk-card'
import { CHIP_CLASS_SM, ChipButton } from '@/components/chefs/chip-button'
import { DeliverableRow } from '@/components/chefs/deliverable-row'
import { DryRunBanner } from '@/components/chefs/dry-run-banner'
import { MealtimeGroupHeader } from '@/components/chefs/mealtime-group-header'
import { MealtimeHeader } from '@/components/chefs/mealtime-header'
import { MissedDeadlineBanner } from '@/components/chefs/missed-deadline-banner'
import { SuppliesTable } from '@/components/chefs/supplies-table'
import type { CampusOrderState } from '@/hooks/chefs/useCampusOrderState'
import type { ServiceDay } from '@/lib/chefs/service-days'
import { campusSupplyRows } from '@/lib/chefs/service-materials'
import { sortDeliverables } from '@/lib/chefs/totals'
import type { Campus, Deliverable } from '@/lib/chefs/types'
import { MEALTIME_ORDER } from '@/lib/chefs/types'

// Each school's final section includes meal supplies and legacy utensils.
// Food container packaging stays with the order rows.

interface OrderSummaryCardProps {
  campus: Campus
  defaultOpen?: boolean
  /** Days within the active date range when this campus has no service. */
  offDays?: ServiceDay<Campus>[]
  /** Per-week submission state derived by `useCampusOrderState`. */
  orderState?: CampusOrderState
  onSelectDeliverable: (deliverable: Deliverable) => void
  selectedDeliverableId?: string
}

function formatOffDayList(days: ServiceDay<Campus>[]): string {
  const items = days.map((d) => `${d.weekday} ${d.label}`)
  if (items.length <= 1) {
    return items.join('')
  }
  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`
  }
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

export function OrderSummaryCard({
  campus,
  defaultOpen = false,
  offDays = [],
  orderState,
  onSelectDeliverable,
  selectedDeliverableId,
}: OrderSummaryCardProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [campusInfoOpen, setCampusInfoOpen] = useState(false)

  const orderedMealtimes = useMemo(() => {
    return [...campus.mealtimes].sort((a, b) => MEALTIME_ORDER.indexOf(a.mealtime) - MEALTIME_ORDER.indexOf(b.mealtime))
  }, [campus.mealtimes])

  const containerClassName = 'overflow-hidden rounded-2xl border border-border bg-background shadow-xs'

  const toggle = () => setOpen((v) => !v)
  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggle()
    }
  }

  if (orderState?.status === 'missed') {
    return (
      <div className={cn('relative', containerClassName)}>
        <div className="flex flex-col gap-4 px-4 py-4 lg:px-6 lg:py-5">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {campus.organizationName}
            </span>
            <span className="flex flex-wrap items-center gap-2 font-normal text-foreground">
              <span>
                {campus.name}
                <span className="font-normal text-foreground"> · </span>
                <span className="font-normal text-foreground">{campus.info.addressLine1}</span>
              </span>
              <ChipButton Icon={Info} className={CHIP_CLASS_SM} onClick={() => setCampusInfoOpen(true)}>
                Delivery instructions
              </ChipButton>
            </span>
          </div>
          <MissedDeadlineBanner
            campus={campus}
            deadlineLabel={orderState.deadlineLabel}
            daysOverdue={orderState.daysOverdue}
          />
        </div>
        <CampusInfoDialog campus={campus} open={campusInfoOpen} onOpenChange={setCampusInfoOpen} />
      </div>
    )
  }

  return (
    <div className={cn('relative', containerClassName)}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className={cn('bg-background', open && 'sticky top-0 z-10')}>
          <div
            role="button"
            tabIndex={0}
            onClick={toggle}
            onKeyDown={handleKey}
            className="flex cursor-pointer items-center justify-between gap-3 px-4 py-4 transition-colors hover:bg-muted/30 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset lg:px-6 lg:py-5">
            {/* No campus-level totals line here (PROD-1342): the "N Meals ·
                X Main Y Side" summary counted PACKAGES per type against a
                true meal count, so it read as wrong ("20 total" over "12
                Main"). The mealtime headers carry no meal count either
                (PROD-1502): only the mealtime and its serve day. */}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {campus.organizationName}
              </span>
              <span className="flex flex-wrap items-center gap-2 font-normal text-foreground">
                <span>
                  {campus.name}
                  <span className="font-normal text-foreground"> · </span>
                  <span className="font-normal text-foreground">{campus.info.addressLine1}</span>
                </span>
                {offDays.length > 0 ? (
                  <span
                    aria-label={`No service on ${formatOffDayList(offDays)}`}
                    className="inline-flex items-center rounded-full bg-amber-100 p-1 text-amber-800">
                    <AlertTriangle className="h-3 w-3" strokeWidth={2.25} />
                  </span>
                ) : null}
              </span>
            </div>

            <span
              aria-hidden
              className="hidden w-36 shrink-0 items-center justify-between gap-1.5 rounded-full bg-muted px-3.5 py-1.5 text-sm font-medium text-foreground lg:inline-flex">
              {open ? 'Hide details' : 'Show details'}
              <ChevronDown
                className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')}
                strokeWidth={1.5}
              />
            </span>
            <ChevronDown
              aria-hidden
              className={cn(
                'h-5 w-5 shrink-0 text-muted-foreground transition-transform lg:hidden',
                open && 'rotate-180',
              )}
              strokeWidth={1.5}
            />
          </div>
        </div>

        <CollapsibleContent>
          <div className="bg-muted/70 pt-4 lg:pt-6">
            {/* The dry-run warning leads: a chef must know this is a test cook
                before they read anything about delivering it. */}
            {campus.isDryRun ? (
              <DryRunBanner className="mx-4 mb-4 lg:mx-6" serviceType={campus.dryRunServiceType} />
            ) : null}
            <div className="mx-4 mb-4 lg:mx-6">
              <ChipButton Icon={Info} className={CHIP_CLASS_SM} onClick={() => setCampusInfoOpen(true)}>
                Delivery instructions
              </ChipButton>
            </div>
            {offDays.length > 0 ? (
              <div className="mx-4 mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 lg:mx-6">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
                <span>No service on {formatOffDayList(offDays)}.</span>
              </div>
            ) : null}
            <div className="flex flex-col gap-4 px-4 pb-6 lg:px-6 lg:pb-8">
              {/* Milk is its own card, on top of the mealtimes (PROD-1502). */}
              <CampusMilkCard campus={campus} />
              {orderedMealtimes.map((block) => (
                <Card
                  key={block.mealtime}
                  className="flex h-full flex-col overflow-hidden rounded-[10px] border-border bg-background py-0 shadow-none">
                  <MealtimeHeader mealtime={block.mealtime} serveDays={block.serveDays} />
                  {/* Order-group sections (PROD-1296): a campus that orders by
                      age group packs each band separately, so its rows render
                      under a per-band heading. */}
                  {block.groups ? (
                    block.groups.map((group) => (
                      <div key={group.key} className="flex flex-col">
                        <MealtimeGroupHeader group={group} showCount={false} />
                        <div className="flex flex-col divide-y divide-border">
                          {sortDeliverables(group.deliverables).map((d) => (
                            <DeliverableRow
                              key={d.id}
                              deliverable={d}
                              onClick={() => onSelectDeliverable(d)}
                              selected={selectedDeliverableId === d.id}
                            />
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col divide-y divide-border">
                      {sortDeliverables(block.deliverables).map((d) => (
                        <DeliverableRow
                          key={d.id}
                          deliverable={d}
                          onClick={() => onSelectDeliverable(d)}
                          selected={selectedDeliverableId === d.id}
                        />
                      ))}
                    </div>
                  )}
                </Card>
              ))}
              {/* Keep this school's explicit supplies and legacy utensils together.
                  SuppliesTable excludes food containers from this separate list. */}
              <SuppliesTable rows={campusSupplyRows([campus])} />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <CampusInfoDialog campus={campus} open={campusInfoOpen} onOpenChange={setCampusInfoOpen} />
    </div>
  )
}
